import { IDataFileVisitor } from './IDataFileVisitor.js';
import {
  Doc,
  DocCompatibilityTable,
  DocExternalField,
  DocJsFilter,
  DocOclConstraint,
  DocOclFilter,
  DocOption,
  DocOptionGroup,
  DocProduct,
  DocProductGroup
} from './DataFile.js';
import { OptionsRegistry } from '../OptionsRegistry.js';
import { Schema } from '../../schema.js';
import { CompatibilityTable } from '../../compatibility/CompatibilityTable.js';
import { MLString, SystemType, TypeKind, TypeReference } from '../../types.js';
import {
  LazySourcedValue,
  ListSourcedValue,
  Option,
  OptionGroup, OptionId,
  Source,
  SourcedValue,
  SourcedValueDefinition
} from '../../option.js';
import { SourceUtils } from '../../internal/SourceUtils.js';
import { DeepModelWalker } from '../../internal/ModelWalker.js';
import { pathToRoot, PropertyValue } from '../../internal/PropertyPathWalker.js';
import { JsonPath } from '../../internal/JsonPath.js';
import { PropertyPath } from '../../engine.js';
import { CompatibilityService } from '../../compatibility/CompatibilityService.js';
import { isOption, isRestrictedTo } from '../../compatibility/CompatibleValues.js';
import { createExpressionConstraint, ExpressionConstraint } from '../../constraint.js';
import { optionIdSymbol } from '../../engine-impl.js';
import { Products } from '../AsyncJsDatasetService.js';
import { ProductDefinition } from '../yaml-models.js';
import { Product, ProductGroup } from '../../product.js';
import { OclConstraintRegistry } from '../../services/OclConstraintRegistry.js';
import { FilterRegistry } from '../../services/FilterRegistry.js';
import { ExternalParser } from '../../parsers/ExternalParser.js';
import { ExternalFieldRegistry } from '../../services/ExternalFieldRegistry.js';
import { CompatibilityTableParser } from '../../compatibility/CompatibilityTableParser.js';

type CompatibilityTablesRegistry = Map<string, CompatibilityTable>;

export class ParsingDataFileVisitor implements IDataFileVisitor {
  constructor(
    private readonly optionsRegistry: OptionsRegistry,
    private readonly products: Products,
    private readonly constraintRegistry: OclConstraintRegistry,
    private readonly filterRegistry: FilterRegistry,
    private readonly externalParser: ExternalParser,
    private readonly externalFieldRegistry: ExternalFieldRegistry,
    private readonly schema: Schema,
    private readonly compatibilityTables: CompatibilityTablesRegistry
  ) {
  }

  visitCompatibilityTable(doc: DocCompatibilityTable): void {
    const parser = new CompatibilityTableParser(doc);
    const factory = parser.parse();
    const table = factory.createCompatibilityTable(this.schema, this.optionsRegistry);
    table.injectExternalFieldRegistry(this.externalFieldRegistry);
    this.compatibilityTables.set(factory.name, table);
  }

  visitExternalFiled(doc: DocExternalField): void {
    this.externalParser.parseField(doc);
    this.externalFieldRegistry.addExternalField(doc);
  }

  visitJsFilter(doc: DocJsFilter): void {
    this.filterRegistry.addFilter(doc);
  }

  visitOclConstraint(doc: DocOclConstraint): void {
    this.constraintRegistry.addConstraint(doc);
  }

  visitOclFilter(doc: DocOclFilter): void {
    this.filterRegistry.addFilter(doc);
  }

  visitOption(doc: DocOption, lazy = true): void {
    if (this.optionsRegistry.hasOption(doc._id)) {
      console.log(`Option id ${doc._id} already exists. Overwrite`);
    }

    this.optionsRegistry.registerOption(doc._id, doc.type, () => this.createOption(doc, lazy));
  }

  visitOptionGroup(doc: DocOptionGroup): void {
    if (this.optionsRegistry.hasOptionGroup(doc._id)) {
      console.log(`OptionGroup id ${doc._id} already exists. Overwrite`);
    }
    let x = {};
    x[optionIdSymbol] = doc._id;
    let optionGroupModel = Object.assign(x, doc.model);
    this.buildModel(optionGroupModel, doc.type);
    const optionsGroup = new OptionGroup(
      this.schema.getSchemaTypeReference(doc.type),
      doc.children.map((c: string) => this.optionsRegistry.getOptionOrOptionGroupById(c)),
      optionGroupModel,
      doc._id);
    this.optionsRegistry.addOptionGroup(doc._id, optionsGroup);
  }

  visitProduct(doc: DocProduct): void {
    if (this.products[doc._id]) {
      console.log(`Product or ProductGroup id ${doc._id} already exists. Overwrite`);
    }
    this.products[doc._id] = this.createProduct(doc);
  }

  visitProductGroup(doc: DocProductGroup): void {
    if (this.products[doc._id]) {
      console.log(`Product or ProductGroup id ${doc._id} already exists. Overwrite`);
    }
    this.products[doc._id] = this.createProductGroup(doc);
  }

  visitUnknown(doc: Doc): void {
    if (doc.kind) {
      console.log('Doc kind is not defined : ' + doc.kind);
    } else {
      console.log('Invalid document', JSON.stringify(doc).slice(0, 15), '...');
    }
  }

  beforeAll(): void {
  }

  afterAll(): void {
  }

  createProductGroup(productGroup: { type: string, _id: string, model: any, children: string [] }): ProductGroup {
    SourceUtils.setProductGroupId(productGroup.model, productGroup._id);
    this.buildModel(productGroup.model, productGroup.type);
    return new ProductGroup(this.schema.getSchemaTypeReference(productGroup.type), productGroup.children.map(child => this.mapChildren(child)), productGroup.model, productGroup._id);
  }

  createProduct(product: ProductDefinition) {
    let model = product.model;
    this.buildModel(model, product.type);
    SourceUtils.setProductId(model, product._id);
    const registryConstraints = this.constraintRegistry.getConstraintsForProductType(product.type);
    let productConstraints = product.constraints ? product.constraints.map((e: any) => createExpressionConstraint(e, this.schema)) : [];
    const constraints = registryConstraints.concat(productConstraints);
    return new Product(this.schema.getSchemaTypeReference(product.type), model, constraints, product._id, this.filterRegistry);
  }

  createOption(doc: { kind: 'Option', type: TypeReference, _id: string, model: any }, lazySources = true): Option {
    this.buildModel(doc.model, doc.type, lazySources);
    // Add id to the model
    SourceUtils.setOptionId(doc.model, doc._id);
    return new Option(this.schema.getSchemaTypeReference(doc.type), doc.model, doc._id);
  }

  mapChildren(child: string): Product | ProductGroup {
    if (!this.products[child]) {
      throw new Error(`Cannot find child id=${child}`);
    }
    return this.products[child];
  }

  buildModel(model: any, type: TypeReference, lazySources = true) {
    let walker = new DeepModelWalker(this.schema, model, type, pValue => !pValue.property.opts.needSource);
    let propertyValue: PropertyValue | undefined;
    while (propertyValue = walker.next()) {
      // Create MLString
      if (propertyValue.property.type.toLowerCase() === SystemType.MLString.toLowerCase()) {
        propertyValue.parent!.value[propertyValue.property.name] = new MLString(propertyValue.value);
      }
      // Handle default property values
      if (propertyValue.value === undefined) {

        propertyValue.parent!.value[propertyValue.property.name] = propertyValue.property.opts.default;
      }
      // if (propertyValue.property.type.toLowerCase() === SystemType.BigNumber.toLowerCase()) {
      //   if (propertyValue.value !== undefined) {
      //     propertyValue.parent!.value[propertyValue.property.name] = new BigNumber(propertyValue.value);
      //   } else {
      //     propertyValue.parent!.value[propertyValue.property.name] = new BigNumber(propertyValue.property.opts.default);
      //   }
      // }
      if (propertyValue.property.opts.needSource) {
        if (!propertyValue.value) {
          continue;
        }
        const propertyPath = pathToRoot(propertyValue, false);
        if (Array.isArray(propertyValue.property.opts.needSource)) {
          propertyValue.parent!.value[propertyValue.property.name] = this.createListSourcedValue(propertyValue.value);
        } else {
          if (propertyValue.property.opts.needSource === 'specific' && this.schema.getType(propertyValue.property.type).kind == TypeKind.List) {
            propertyValue.parent!.value[propertyValue.property.name] = propertyValue.value.map(y => this.createSourcedValue(
              propertyValue.property.type,
              y,
              propertyPath, lazySources));
          } else {
            propertyValue.parent!.value[propertyValue.property.name] = this.createSourcedValue(
              propertyValue.property.type,
              propertyValue.value,
              propertyPath, lazySources);
          }
        }
      }
    }
  }

  createSourcedValue(
    typeRef: TypeReference,
    obj: {
      optionAvailability?: [],
      value: string | string[],
      source: SourcedValueDefinition
    },
    propertyPath: PropertyPath,
    lazy = true): SourcedValue {
    let optionAvail = obj.optionAvailability ? obj.optionAvailability.map((e: any) => createExpressionConstraint(e, this.schema)) : [];
    const sourcesFn = () => {
      let sources: (Option | OptionGroup)[] = [];
      if ('match' in obj.source) {
        if (obj.source.match === '$type') {
          sources = this.optionsRegistry.findOptionsWithType(typeRef);
        } else {
          if ('compatibilityTables' in obj.source.match && Array.isArray(obj.source.match.compatibilityTables)) {
            const tables = obj.source.match.compatibilityTables;
            const tablesToUse = tables.map(t => [t, this.compatibilityTables.get(t)]);
            const service = new CompatibilityService(this.schema);
            tablesToUse.forEach(([tableName, table]) => service.addCompatibilityTable(table, tableName));
            const values = service.getAvailableCompatibleValues(propertyPath);
            if (isRestrictedTo(values)) {
              for (let value of values.values) {
                if (isOption(value)) {
                  sources.push(value);
                }
              }
            } else {
              // Return all options for this type
              sources = this.optionsRegistry.findOptionsWithType(typeRef);
            }
          }
        }
      } else if (Array.isArray(obj.source)) {
        sources = obj.source.map((x: string) => this.optionsRegistry.getOptionOrOptionGroupById(x));
      } else {
        throw new Error(`Invalid source for ${JSON.stringify(obj.value)}`);
      }
      return sources;
    };

    const values = Array.isArray(obj.value)
      ? obj.value.map((id: string) => this.optionsRegistry.getOptionById(id))
      : this.optionsRegistry.getOptionById(obj.value);

    if ('match' in obj.source && obj.source.match === '$type') {
      const sourceIds = this.optionsRegistry.findOptionsIdsWithType(typeRef);
      return makeSourcedValue(lazy, values, sourceIds, sourcesFn, optionAvail);
    }
    if (Array.isArray(obj.source)) {
      return makeSourcedValue(lazy, values, obj.source, sourcesFn, optionAvail);
    }
    return new SourcedValue(values, sourcesFn(), optionAvail);
  }

  createListSourcedValue(obj: { value: any[], source: {} }): ListSourcedValue {
    let vals: any[] = [];
    for (let propVal of obj.value) {
      for (let src of Object.keys(obj.source)) {
        new JsonPath(propVal)
          .update(src, oldValue => this.optionsRegistry.getOptionOrOptionGroupById(oldValue));
        // propVal[src] = mapOption(options, propVal[src]);
      }
      vals.push(propVal);
    }
    let srcs: { [s: string]: Source } = {};
    for (let propSrc in obj.source) {
      srcs[propSrc] = new Source(
        obj.source[propSrc].source.map((x: string) => this.optionsRegistry.getOptionOrOptionGroupById(x)),
        obj.source[propSrc].optionAvailability ? obj.source[propSrc].optionAvailability.map((e: any) => createExpressionConstraint(e, this.schema)) : []
      );
    }

    return new ListSourcedValue(
      vals,
      srcs
    );
  }
}

function makeSourcedValue(lazy: boolean,
                          values: Option[] | Option,
                          sourceIds: OptionId[],
                          sourcesFn: () => (Option | OptionGroup)[],
                          optionAvail: ExpressionConstraint[]): SourcedValue {
  if (lazy) {
    return new LazySourcedValue(
      values,
      sourceIds,
      sourcesFn,
      optionAvail
    );
  } else {
    return new SourcedValue(values, sourcesFn(), optionAvail);
  }
}


